import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { MotifFormField, MotifSelect, MotifButton } from '@ey-xd/motif-react';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';

/**
 * ViewReportHeader - header area for Analytics View Report page
 * Renders the report selection dropdown in the page header.
 */
const ViewReportHeader = ({
  selectedReportId,
  analyticsReports,
  onReportChange,
  onRunReport,
  reits,
  resetCheckboxes,
  reportPeriods,
  checkedPeriods,
}) => {
  const reitOptions = Array.isArray(reits)
    ? reits.map((r) => ({
        key: r.reitid,
        value: r.reitid,
        text: r.reitShortName,
      }))
    : [];

  const reportOptions = Array.isArray(analyticsReports)
    ? analyticsReports.map((report) => ({
        key: report.analyticsReportGUID,
        value: report.analyticsReportGUID,
        text: report.analyticsReportName,
      }))
    : [];

  const [selectedReitIds, setSelectedReitIds] = useState([]);
  const [selectedReportDescriptions, setSelectedReportDescriptions] = useState(
    []
  );

  const uniquePeriodDescriptions = Array.isArray(reportPeriods)
    ? Array.from(
        new Set(reportPeriods.map((rp) => rp.reportPeriodDescription))
      ).map((periodDesc) =>
        reportPeriods.find((rp) => rp.reportPeriodDescription === periodDesc)
      )
    : [];

  const reportPeriodOptions = uniquePeriodDescriptions.map((rp) => ({
    key: rp.reportPeriodID,
    value: rp.reportPeriodDescription,
    text: rp.reportPeriodDescription,
    isSelected: Array.isArray(checkedPeriods)
      ? checkedPeriods.find((cp) => cp.key === rp.reportPeriodID)?.isSelected ||
        false
      : false,
  }));

  useEffect(() => {
    setSelectedReitIds([]);
    setSelectedReportDescriptions([]);
  }, [resetCheckboxes]);

  const handleReitChange = (event) => {
    if (!Array.isArray(event) || event.length === 0) {
      setSelectedReitIds([]);
      return;
    }
    const nextSelectedReitIds = [];
    for (let i = 0; i < event.length; i++) {
      const value = event[i];
      if (value !== null && value !== undefined) {
        nextSelectedReitIds.push(String(value));
      }
    }
    setSelectedReitIds(nextSelectedReitIds);
  };

  const handleReportChange = (event) => {
    if (!Array.isArray(event) || event.length === 0) {
      setSelectedReportDescriptions([]);
      return;
    }
    const tempSelectedReportDescriptions = [];
    for (let i = 0; i < event.length; i++) {
      const value = event[i];
      if (value !== null && value !== undefined) {
        const allowValue =
          !selectedReitIds || selectedReitIds.length === 0
            ? true
            : Array.isArray(reportPeriods)
              ? reportPeriods.some(
                  (rp) =>
                    rp.reportPeriodDescription === value &&
                    selectedReitIds.includes(String(rp.reitid))
                )
              : false;
        if (allowValue) {
          tempSelectedReportDescriptions.push(value);
        }
      }
    }
    setSelectedReportDescriptions(tempSelectedReportDescriptions);
  };

  const canRunReport =
    Boolean(selectedReportId) &&
    selectedReitIds.length > 0 &&
    selectedReportDescriptions.length > 0;

  return (
    <MotifFormField>
      <div className="row mt-4 align-items-center mb-5">
        <div className="col-md-4">
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Field
              name="analyticsReport"
              label="Select Analytics Report (Required)"
              isRequiredField={true}
              validate={[formValidators.required, formValidators.minLength2]}
              onChange={onReportChange}
              options={reportOptions}
              optionText={(r) => r.text}
              optionValue={(r) => r.value}
              component={formHelpers.SingleSelectComponent}
              testingData={{
                label: 'lblSelectAnalyticsReport',
                div: 'divSelectAnalyticsReport',
              }}
              data-testid="btnSelectAnalyticsReport"
            />
          </div>
        </div>

        <div className="col-md-4">
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Field
              name="reit"
              label="Select REIT (Required)"
              isRequiredField={true}
              validate={[formValidators.required, formValidators.minLength2]}
              onChange={handleReitChange}
              options={reitOptions}
              optionText={(r) => r.text}
              optionValue={(r) => r.value}
              component={formHelpers.MultipleSelect}
              testingData={{ label: 'lblSelectREITs', div: 'divSelectREITs' }}
              showSelectAllButton={true}
              data-testid="btnSelectREITs"
            />
          </div>
        </div>

        <div className="col-md-2">
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Field
              name="reportPeriod"
              label="Select Report Period (Required)"
              isRequiredField={true}
              validate={[formValidators.required, formValidators.minLength2]}
              onChange={handleReportChange}
              options={reportPeriodOptions}
              optionText={(rp) => rp.text}
              optionValue={(rp) => rp.value}
              optionKey={(r) => r.key}
              component={formHelpers.MultipleSelect}
              testingData={{
                label: 'lblSelectReportPeriod',
                div: 'divSelectReportPeriod',
              }}
              showSelectAllButton={true}
              data-testid="btnSelectReportPeriod"
            />
          </div>
        </div>

        <div className="col-md-2 mt-5 d-flex justify-content-end">
          <MotifButton
            disabled={!canRunReport}
            variant="primary"
            onClick={onRunReport}
            data-testid="btnRunReport"
          >
            Run Report
          </MotifButton>
        </div>
      </div>
    </MotifFormField>
  );
};

ViewReportHeader.propTypes = {
  onReportChange: PropTypes.func,
  onRunReport: PropTypes.func,
  resetCheckboxes: PropTypes.bool,
  reits: PropTypes.array,
  reportPeriods: PropTypes.array,
  checkedPeriods: PropTypes.array,
};

export default reduxForm({
  form: 'analyticsViewReportHeader',
  enableReinitialize: true,
})(ViewReportHeader);
